import { select as d3_select, create as d3_create } from 'd3-selection'
import { xml as d3_xml } from 'd3-fetch'
import { isDarkMode, mergeConfig } from '../utils/configUtils'

import {
  divToSVGText,
  FONTS,
  generateFontStyleElement,
  getPath,
  textToSVGText,
} from '../utils/fontUtils'
import RootBehavior from '../viz_behaviors/RootBehavior'
import LegendBehavior from '../viz_behaviors/LegendBehavior'
import TrendLegendBehavior from '../viz_behaviors/TrendLegendBehavior'
import TooltipBehavior from '../viz_behaviors/TooltipBehavior'
import MultiDataPickerBehavior from '../viz_behaviors/MultiDataPickerBehavior'
// import GridModeBehavior from '../viz_behaviors/GridModeBehavior'
// import detector from '../utils/textUtils'
import { dentedRect } from '../utils/pathUtils'
// import {formatNumber} from '../utils/formatUtils'
// import santander_logo from '../styles/icons/Banco_santander_logo.svg'
// import santander_logo from '../styles/icons/Logo-Santander.svg'
import { LOGOS, setFills } from '../utils/logoUtils'
import * as COLORS from '../utils/colorUtils'
import AnnotationBehavior from '../viz_behaviors/AnnotationBehavior'
import { svgfontCheck } from '../utils/fontUtils'
import { sanitizeData } from '../utils/dataUtils'
import MultiDataDataController from '../viz_behaviors/MultiDataDataController'
import AccessibilityBehavior from '../viz_behaviors/AccessibilityBehavior'
import { ACCESSIBILITY_OPTIONS_KEYS } from '../utils/accessibilityUtils'
import GuideLinesBehavior from '../viz_behaviors/GuideLinesBehavior'
export default class BaseViz {
  static defaultConfig = {
    /**
     * {-EN-} Skin of the chart. Background and default colorscheme will change according to the skin.
     * {-ES-} Piel del gráfico. El fondo y el esquema de colores predeterminado cambiarán según la apariencia.
     */
    skin: {
      type: String,
      default: 'light',
      options: ['light', 'dark'],
      exposed: true,
    },
    /**
     * {-EN-} Main title of the chart.
     * {-ES-} Título principal del gráfico.
     */
    title: {
      type: String,
      default: 'Title',
      exposed: true,
    },
    /**
     * {-EN-} Main title of the chart.
     * {-ES-} Título principal del gráfico.
     */
    title: {
      type: String,
      default: 'Title',
      exposed: true,
    },
    /**
     * {-EN-} Subtitle of the chart.
     * {-ES-} Subtítulo del gráfico.
     */
    subTitle: {
      type: String,
      default: 'Subtitle',
      exposed: true,
    },
    /**
     * {-EN-} Show or hide the "Santander" logo on the bottom-right of the chart.
     * {-ES-} Muestra u oculta el logo de "Santander" en la parte inferior derecha del gráfico.
     */
    showLogo: {
      type: [String, Boolean, null],
      default: 'santander_logo',
      options: LOGOS.map(d => d.id).concat([true, false, null]), //el bueno es el null, dejamos el true/false por retrocomp
      exposed: true,
    },
    /**
     * {-EN-} Text to credit the source of the data. Leave blank (`''`) to hide.
     * {-ES-} Texto para acreditar la fuente de los datos. Dejar en blanco (`''`) para esconder.
     */
    sourceCredits: {
      type: String,
      default: 'default data source',
      exposed: true,
    },
    /**
     * {-EN-} Link to the source credits.
     * {-ES-} Enlace a los créditos fuente.
     */
    sourceLink: {
      type: String,
      default: '',
      exposed: true,
    },
    /**
     * {-EN-} Delimiter character for thousands
     * {-ES-} Carácter delimitador para miles
     */
    thousandsDelimiter: {
      type: [String, Array, Function, null],
      default: null,
      exposed: true,
    },
    /**
     * {-EN-} Delimiter character for decimals
     * {-ES-} Carácter delimitador para decimales
     */
    decimalDelimiter: {
      type: [String, Array, Function, null],
      default: null,
      exposed: true,
    },
    /**
     * {-EN-} Locale language support (Support English or Spanish at the moment)
     * {-ES-} Soporte de idioma local (Soporta inglés o español por el momento)
     */
    locale: {
      type: [String],
      default: 'en-US',
      options: ['es-ES', 'en-US', 'pt-BR', 'pl-PL'],
    },
    currentPage: {
      type: [String, Number, null],
      default: null,
    },
  }

  static behaviors = [
    RootBehavior,
    TooltipBehavior,
    LegendBehavior,
    TrendLegendBehavior,
    AnnotationBehavior,
    MultiDataPickerBehavior,
    AccessibilityBehavior,
    GuideLinesBehavior,
  ]

  constructor($root) {
    //,config) { ???

    const resizeObserver = new ResizeObserver(() => {
      //chapuzilla para esconder scrolls on resize
      d3_select(this.$annotationsContainer).style('display', 'none')
      setTimeout(this.afterChartUpdate.bind(this), 100)
    })
    resizeObserver.observe($root)

    this.$root = $root
    this.$root.style.border = 'solid 1px #DEEDF2'

    d3_select($root).style('overflow', 'hidden')

    let contentWrapper = d3_select($root)
      .append('div')
      .attr('class', 'wrapper')
      .style('margin', '16px')
      .style('width', 'calc(100% - 32px)')
      .style('height', 'calc(100% - 32px)')

    contentWrapper
      .style('display', 'flex')
      .style('flex-direction', 'column')
      .style('position', 'relative')
      .style('font-family', 'santander-microtext-embedded')
      .style('user-select', 'none')

    let headerRoot = contentWrapper
      .append('div')
      .attr('class', 'header-container')
      .style('display', 'flex')
      .style('align-items', 'center')

    let headerLeftContainer = headerRoot
      .append('div')
      .attr('class', 'left-container')
      .style('text-align', 'left')
      .style('position', 'relative')

    this.$title = headerLeftContainer
      .append('div')
      .attr('class', 'title')
      .style('font-size', '22px')
      .style('font-family', 'santander-headline-bold-embedded')
      // .style('font-weight','bold')
      .node()
    this.$separator = headerLeftContainer
      .append('div')
      .attr('class', 'separator rect')
      .style('width', '28px')
      .style('height', '3px')
      .style('background-color', COLORS.PRIMARY_COLOR)
      .style('margin', '10px 0 7px 0')
      .node()
    this.$subTitle = headerLeftContainer
      .append('div')
      .attr('class', 'sub-title')
      .style('font-size', '18px')
      .style('font-weight', 'regular')
      .node()
    // this.$headerRoot = headerRoot.node()

    this.$headerRightContainer = headerRoot
      .append('div')
      .attr('class', 'right-container')
      .style('margin-left', 'auto')
      .node()

    this.$chartContainer = contentWrapper
      .append('div')
      .attr('class', 'chart-container')
      .style('width', '100%')
      .style('flex', '1 1 auto')
      .style('display', 'flex')
      .style('position', 'relative')
      .append('div')
      .attr('class', 'chart-scroller')
      .style('width', '100%')
      .style('height', '100%')
      .style('flex', '1 1 auto')
      .style('position', 'absolute')
      // .style('overflow','hidden')
      .node()
    // this.$contentWrapper = contentWrapper.node()

    this.$legendRoot = contentWrapper.append('div').attr('class', 'legend-container').node()
    this.$trendLegendRoot = contentWrapper
      .append('div')
      .attr('class', 'trend-legend-container')
      .node()
    this.$multiDataPickerRoot = contentWrapper
      .append('div')
      .attr('class', 'multi-data-picker-container')
      .node()

    let footerRoot = contentWrapper
      .append('div')
      .attr('class', 'footer-container')
      .style('display', 'flex')
      .style('justify-content', 'space-between')
      .style('align-items', 'flex-end')
      .style('position', 'relative')
      .style('margin-top', '10px')
    this.$sourceRoot = footerRoot
      .append('div')
      .attr('class', 'source-container')
      .style('font-size', '12px')
      .style('line-height', '10px')
      .node()

    this.$logoRoot = footerRoot.append('div').attr('class', 'logo').node()
    this.$logoRoot.style.marginLeft = 'auto'
  }
  get data() {
    return this._data
  }
  set data(value) {
    if (value.isMulti) {
      this._data = {
        isMulti: true,
        data: value.data.map(v => ({ id: v.id, data: sanitizeData(v.data) })),
      }
    } else {
      // for retrocomp!
      if (value.data) this._data = { data: sanitizeData(value.data) }
      else this._data = { data: sanitizeData(value) }
      // this._data = {data: sanitizeData(value.data)}
    }
    this.beforeChartUpdate()
    //Estaba claramente mal, no?! (aplicaba el datacontroller al "value" y no al sanitized this._data)
    this.setChartData(this._data)
    this.afterChartUpdate()
  }

  get config() {
    return this._config
  }
  set config(value) {
    this._config = value
    this.beforeChartUpdate()
    //Dangerous, pero se necesita no?
    // Se necesita por ejemplo para el "minBarHeight" de los barcharts...
    // entiendo qeu seteando el data directamente (_data) y luego el config por el setter solo se pinta una vez, no?!
    if (this.data) {
      // this.setChartData(this.data)
      this.setChartConfigAndData(this.config, this.data)
    } else {
      this.setChartConfig(this.config)
    }
    // this.chart.config = this.config
    this.afterChartUpdate()
  }

  get hiddenItems() {
    return this._hiddenItems || []
  }
  set hiddenItems(value) {
    this._hiddenItems = value
    this.beforeChartUpdate()
    //Estaba claramente mal, no?! (aplicaba el datacontroller al "value" y no al sanitized this._data)
    // this.chart.data = this.applyDataController(this._data)
    this.setChartData(this._data)
    this.afterChartUpdate()
  }

  get accessibilityOptions() {
    if (!this.config.AccessibilityBehavior.showAccessibilityOptions) return null
    return (
      this._accessibilityOptions || {
        [ACCESSIBILITY_OPTIONS_KEYS.darkMode]: this.config.skin === 'dark',
      }
    )
  }

  set accessibilityOptions(value) {
    this._accessibilityOptions = value
    this.beforeChartUpdate()
    this.setChartData(this._data)
    this.afterChartUpdate()
  }

  // setChartData(sourceData) {
  //   let hiddenItems = this.hiddenItems || []
  //   let data = MultiDataDataController(sourceData, this.config.currentPage)

  //   let visibleData = this.__proto__.constructor.dataControllerClass(data, this.config, hiddenItems)
  //   let allData = this.__proto__.constructor.dataControllerClass(data, this.config)

  //   this.chart.allData = allData
  //   this.chart.data = visibleData
  // }
  beforeChartUpdate() {
    RootBehavior(this.$chartContainer, this, this.config)
    /*
    d3_select(this.$root)
      .select('.wrapper')
      .attr('aria-label', this.config.alt || this.config.title)
      */

    this.$root.style.filter = this.accessibilityOptions?.[ACCESSIBILITY_OPTIONS_KEYS.contrast]
      ? 'invert(100%)'
      : 'unset'
    this.$title.innerHTML =
      this.data?.isMulti &&
      this.config.currentPage &&
      this.config.MultiDataPickerBehavior.multiDataMode !== 'smallmultiples'
        ? this.config.title + ' - ' + this.config.currentPage
        : this.config.title
    this.$subTitle.innerHTML = this.config.subTitle

    this.$separator.style.display = this.config.title !== '' ? 'block' : 'none'
    this.$title.style.display = this.config.title !== '' ? 'block' : 'none'
    this.$title.style.fontSize = this.accessibilityOptions?.[
      ACCESSIBILITY_OPTIONS_KEYS.increaseTextSize
    ]
      ? '26px'
      : '22px'
    this.$subTitle.style.display = this.config.subTitle !== '' ? 'block' : 'none'
    this.$subTitle.style.fontSize = this.accessibilityOptions?.[
      ACCESSIBILITY_OPTIONS_KEYS.increaseTextSize
    ]
      ? '22px'
      : '18px'

    this.$sourceRoot.style.fontSize = this.accessibilityOptions?.[
      ACCESSIBILITY_OPTIONS_KEYS.increaseTextSize
    ]
      ? '16px'
      : '12px'

    // config: sourceCredits
    //ya se "quita" con innerHTML = '' :D
    if (this.config.sourceLink && this.config.sourceLink !== '') {
      let propperLink = this.config.sourceLink
      if (propperLink.indexOf('http') < 0) propperLink = 'http://' + propperLink
      this.$sourceRoot.innerHTML = `<a href="${propperLink}" style="color: inherit" target="_blank">${this.config.sourceCredits}</a>`
    } else {
      this.$sourceRoot.innerHTML = this.config.sourceCredits
    }

    // config: showLogo
    let logoData
    if (this.config.showLogo) {
      logoData =
        this.config.showLogo === true ? LOGOS[0] : LOGOS.find(l => l.id === this.config.showLogo)
    }
    let logoContent = d3_select(this.$logoRoot)
      .selectAll('.logo-content')
      .data(logoData ? [logoData] : [], d => d.id)
    let logoEnter = logoContent.enter().append('div').attr('class', 'logo-content')
    logoEnter.each(function (d) {
      let svg = new DOMParser().parseFromString(logoData.svg, 'image/svg+xml').documentElement
      const { x, y, width, height } = svg.viewBox.baseVal
      svg.setAttribute('class', 'logo')
      svg.setAttribute('height', 16)
      svg.setAttribute('width', 16 * (width / height))
      this.append(svg)
    })
    logoContent.exit().remove()

    this.$logoRoot.style.display = this.config.showLogo ? 'block' : 'none'

    this.$sourceRoot.style.display = this.config.sourceCredits !== '' ? 'block' : 'none'

    this.$root.style.transition = 'background-color .8s'
    // console.log('setting skin styles', this.config.skin)
    // config: skin
    if (isDarkMode(this.config, this.accessibilityOptions)) {
      this.$root.style.backgroundColor = COLORS.SKIN_BACKGROUND_DARK
      this.$root.style.color = '#fff'
      this.$title.style.color = '#fff'
      this.$subTitle.style.color = '#ddd'
      this.$sourceRoot.style.color = '#ddd'
      //TODO: SHOULD BRING BACK!
      d3_select(this.$logoRoot)
        .selectAll('.logo-content')
        .select('svg')
        .each(function () {
          this.style.fill = '#ddd'
          setFills(this, '#ddd')
        })
    } else {
      this.$root.style.backgroundColor = COLORS.SKIN_BACKGROUND
      this.$root.style.color = '#888'
      this.$title.style.color = '#222'
      this.$subTitle.style.color = '#666'
      this.$sourceRoot.style.color = '#666'
      //TODO: SHOULD BRING BACK!
      d3_select(this.$logoRoot)
        .selectAll('.logo-content')
        .select('svg')
        .each(function () {
          this.style.fill = '#666'
          setFills(this, '#666')
        })
    }
    MultiDataPickerBehavior(this.$multiDataPickerRoot, this, this.config.MultiDataPickerBehavior)
    // GridModeBehavior(this.$root, this, this.config.GridModeBehavior)
  }
  // WARNING: esto estaba asi, pero al acorn no le gustaba...
  // afterItemsHelper = () => {
  afterItemsHelper() {
    if (this.data) {
      if (this.config.LegendBehavior.showLegend) {
        let icon = (this.config.LegendBehavior && this.config.LegendBehavior.icon) || 'circle'
        // MUST BRING BACK!!!!
        LegendBehavior(this.$legendRoot, this.chart, this.config.LegendBehavior)
      } else {
        this.$legendRoot.innerHTML = ''
      }
      if (this.config.TrendLegendBehavior.showLegend) {
        let icon =
          (this.config.TrendLegendBehavior && this.config.TrendLegendBehavior.icon) || 'line'
        TrendLegendBehavior(this.$trendLegendRoot, this.config.TrendLinesHelper.linesDict, {
          icon: icon,
        })
      } else {
        this.$trendLegendRoot.innerHTML = ''
      }
      // MUST BRING BACK!!!
      TooltipBehavior(this.chart, this.config.TooltipBehavior || {})
    }
  }
  afterChartUpdate() {
    // MUST BRING BACK!!!
    if (this.config && this.config.AnnotationBehavior)
      AnnotationBehavior(this.$annotationsContainer, this, this.config.AnnotationBehavior)
    if (this.config) {
      AccessibilityBehavior(
        this.$headerRightContainer,
        this.chart,
        this.config.AccessibilityBehavior
      )
      GuideLinesBehavior(this.chart.$root, this.chart, this.config.GuideLinesBehavior)
    }
  }

  _getConfig() {
    let behaviorsConfig = this.__proto__.constructor.behaviors.reduce((config, bh) => {
      config[bh.configName] = bh.defaultConfig
      return config
    }, {})
    let baseConfig = mergeConfig(this.__proto__.constructor.defaultConfig, behaviorsConfig)

    let tmp = mergeConfig(baseConfig, this.constructor.defaultConfig)
    let tmp2 = this.constructor.chartClass.prototype._getConfig()
    let config = mergeConfig(tmp, tmp2)
    // TODO: falta mergear los "behaviros" ??
    return config
  }

  getSVG() {
    return svgfontCheck().then(() => this._getSVG.call(this))
  }
  _getSVG() {
    let copyTextStyles = (target, source) => {
      return target
        .attr('alignment-baseline', 'before-edge')
        .style('font-size', source.node().style.fontSize)
        .style('font-weight', source.node().style.fontWeight)
        .style('font-family', source.node().style.fontFamily || 'santander-microtext-embedded')
        .attr('fill', source.node().style.color || 'currentColor')
    }
    let cloneNode = (target, source) => {
      let clone
      let responsePromise
      if (source.node().classList[0] === 'logo-content') {
        let content = source.html()
        clone = target.append('g').html(content)
      } else if (source.node().nodeName === 'svg') {
        let content = source.html()
        clone = target.append('g').html(content)
        let refs = []
        clone
          .node()
          .querySelectorAll('text')
          .forEach($t => refs.push($t))
        source
          .node()
          .querySelectorAll('text')
          .forEach(($t, i) => {
            let t = d3_select($t)
            if (t.attr('display') !== 'none' && !(+t.style('opacity') === 0)) {
              textToSVGText(t, d3_select(refs[i].parentNode))
            }
            refs[i].remove()
          })
        // chapuzilla fills de gradients (safari)
        clone
          .node()
          .querySelectorAll('linearGradient')
          .forEach(h => (h.id = 'cloned-' + h.id))
        clone
          .node()
          .querySelectorAll('.gradient-rect')
          .forEach(h => (h.style.fill = h.style.fill.replace('#', '#cloned-')))
        clone
          .node()
          .querySelectorAll('path')
          .forEach(h => {
            let fill = h.getAttribute('fill')
            if (fill && fill.indexOf('url(#grd') >= 0)
              h.setAttribute('fill', fill.replace('#', '#cloned-'))
          })
      } else if (source.node().children.length === 0) {
        let textContent = source.node().innerHTML
        if (textContent !== '') {
          divToSVGText(source, target)
        } else {
          let rect = source.node().getBoundingClientRect()
          let shape = source.node().classList[1]
          if (shape === 'dented-rect') {
            clone = target
              .append('path')
              .attr('d', () => {
                // return dentedRect(rect.width, rect.height, , null, 4)
                // return dentedRect(rect.width, rect.height, ['br','tl'], null, 3,{tl:0,tr:0,bl:0,br:0}, {tl:4,tr:4,bl:4,br:4}, 4)
                return dentedRect(rect.width, rect.height, {
                  dents: ['br', 'tl'],
                  lengths: { tl: rect.width, tr: rect.width, bl: rect.width, br: rect.width },
                  forcedDentSize: 3,
                  barWidth: rect.width,
                })
              })
              .attr('shape-rendering', 'crispedges')
          } else if (shape === 'rounded-rect') {
            clone = target
              .append('path')
              .attr('class', 'rounded-rect')
              .attr('d', () => {
                let dentSize = source.node().style.borderRadius.replace('px', '')
                return dentedRect(rect.width, rect.height, {
                  dents: ['tr', 'tl', 'bl', 'br'],
                  barWidth: rect.width,
                  lengths: { tl: rect.width, tr: rect.width, bl: rect.width, br: rect.width },
                  forcedDentSize: dentSize,
                })
              })
          } else if (shape) {
            clone = target
              .append(shape)
              .attr('width', rect.width)
              .attr('height', rect.height)
              .attr('r', rect.width / 2)
              .attr('cx', rect.width / 2)
              .attr('cy', rect.height / 2)
              .attr('shape-rendering', shape === 'circle' ? '' : 'crispedges')
          }
          if (clone) {
            clone.attr('fill', source.node().style.backgroundColor || 'none')
            if (source.node().style.borderColor)
              clone.attr('stroke', source.node().style.borderColor)
            if (source.node().style.borderWidth)
              clone.attr('stroke-width', source.node().style.borderWidth.replace('px', ''))
          }
        }
      } else {
        source.node().children.forEach($child => {
          if ($child.style.display !== 'none') {
            clone = target.append('g').attr('class', $child.classList)
            responsePromise = cloneNode(clone, d3_select($child))
          }
        })
      }
      let parentRect = source.node().parentNode.getBoundingClientRect()
      let childRect = source.node().getBoundingClientRect()
      target.attr(
        'transform',
        `translate(${childRect.x - parentRect.x},${childRect.y - parentRect.y})`
      )
      return responsePromise || new Promise(res => res())
    }

    let root = d3_select(this.$root)
    let scroller = d3_select(this.$root.querySelectorAll('.chart-scroller')[0])
    let resizable = d3_select(this.$root.closest('.resizable-component'))
    let prevSize = resizable.style('height')
    if (scroller.node().scrollHeight !== scroller.node().clientHeight) {
      scroller.style('position', 'relative')
      resizable.style('height', 'auto')
    }

    let frame = this.$root.getBoundingClientRect()
    let svg = d3_create('svg')
    svg
      .attr('width', frame.width)
      .attr('height', frame.height)
      .attr('color', this.$root.style.color)

    let result = cloneNode(svg, root).then(() => {
      // startDownload(svg.node(), this.config.title || this.componentLabel)
      svg.attr('transform', null)

      FONTS.forEach(fontDef => {
        let defs = svg.selectAll('defs').data([null])
        defs = defs.merge(defs.enter().append('defs'))
        let svgStyleElement = generateFontStyleElement(fontDef, 'svg')
        defs.node().appendChild(svgStyleElement)
      })
      svg.node().innerHTML = svg.node().innerHTML.replaceAll('currentColor', this.$root.style.color)
      return svg.node()
    })

    scroller.style('position', 'absolute')
    resizable.style('height', prevSize)
    return result
  }
  selectById(id) {
    this.chart && this.chart.selectById && this.chart.selectById(id)
  }
  showTooltipById(id) {
    this.chart && this.chart.showTooltipById && this.chart.showTooltipById(id)
  }
}
